import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, X, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import BlogEditor from '../components/BlogEditor';
import Logo from '../components/Logo';
import { Theme, BlogPost } from '../types';
import toast from 'react-hot-toast';

interface BlogEditProps {
  post?: BlogPost;
  theme: Theme;
  onSave: (post: BlogPost) => void;
}

export default function BlogEdit({ post, theme, onSave }: BlogEditProps) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(post?.title || '');
  const [content, setContent] = useState(post?.content || '');
  const [excerpt, setExcerpt] = useState(post?.excerpt || '');
  const [imageUrl, setImageUrl] = useState(post?.imageUrl || '');
  const [tags, setTags] = useState(post?.tags || []);
  const [tagInput, setTagInput] = useState('');

  const handleSave = () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    const newPost: BlogPost = {
      id: post?.id || Date.now().toString(),
      title,
      content,
      excerpt: excerpt || content.slice(0, 150) + '...',
      author: 'Alexander Orchi',
      date: post?.date || new Date().toISOString(),
      imageUrl,
      tags,
      likes: post?.likes || 0,
      comments: post?.comments || [],
    };

    onSave(newPost);
    toast.success('Blog post saved successfully!');
    navigate('/blog');
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className={`min-h-screen ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/blog')}
            className={`flex items-center gap-2 ${
              theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ArrowLeft size={20} />
            Back to Blog
          </button>
          <div className="flex items-center gap-3">
            <Logo theme={theme} size="small" />
            <h1 className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {post ? 'Edit Blog Post' : 'Create New Post'}
            </h1>
          </div>
          <div className="flex gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/blog')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                theme === 'dark'
                  ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X size={20} />
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center gap-2 hover:bg-purple-700"
            >
              <Save size={20} />
              Save Post
            </motion.button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            <div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Post Title"
                className={`w-full px-4 py-3 text-xl font-bold rounded-lg ${
                  theme === 'dark'
                    ? 'bg-gray-800 text-white border-gray-700'
                    : 'bg-white text-gray-900 border-gray-200'
                }`}
              />
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Post excerpt (optional)"
                  rows={3}
                  className={`w-full px-4 py-3 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-gray-800 text-white border-gray-700'
                      : 'bg-white text-gray-900 border-gray-200'
                  }`}
                />
              </div>
              <div className="w-1/3">
                <div className={`p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className="flex items-center gap-2 mb-4">
                    <ImageIcon size={20} className="text-purple-500" />
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      Featured Image
                    </span>
                  </div>
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Image URL"
                    className={`w-full px-3 py-2 rounded ${
                      theme === 'dark'
                        ? 'bg-gray-700 text-white border-gray-600'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    }`}
                  />
                </div>
              </div>
            </div>

            <div>
              <BlogEditor
                content={content}
                onChange={setContent}
                theme={theme}
              />
            </div>

            <div className={`p-4 rounded-lg ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex flex-wrap gap-2 mb-4">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className={`px-3 py-1 rounded-full flex items-center gap-1 ${
                      theme === 'dark'
                        ? 'bg-gray-700 text-purple-300'
                        : 'bg-purple-100 text-purple-700'
                    }`}
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  placeholder="Add tags..."
                  className={`flex-1 px-3 py-2 rounded ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-white border-gray-600'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                  }`}
                />
                <button
                  onClick={addTag}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  Add Tag
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}